import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { ArrowLeft, MessageSquare, Bell, Send, Users, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AdminMessages = ({ user, setUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showTrainingReminderDialog, setShowTrainingReminderDialog] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Training Reminder state
  const [trainingReminderData, setTrainingReminderData] = useState({
    recipientType: 'all',
    messageType: 'general'
  });
  
  // General Message state
  const [messageData, setMessageData] = useState({
    recipients: 'all',
    subject: '',
    message: ''
  });

  const handleSendTrainingReminder = async () => {
    setSending(true);
    try {
      await axiosInstance.post('/admin/send-training-reminders', trainingReminderData);
      toast.success(t('adminToasts.TrainingRemindersSentSuccessfully'));
      setShowTrainingReminderDialog(false);
      setTrainingReminderData({ recipientType: 'all', messageType: 'general' });
    } catch (error) {
      console.error('Failed to send training reminders:', error);
      toast.error(error.response?.data?.detail || 'Failed to send training reminders');
    } finally {
      setSending(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageData.subject.trim() || !messageData.message.trim()) {
      toast.error(t('adminToasts.PleaseFillInSubjectAndMessage'));
      return;
    }

    setSending(true);
    try {
      console.log('Fetching users for recipients:', messageData.recipients);
      
      // Fetch users based on recipient selection
      let targetUserIds = [];
      
      if (messageData.recipients === 'all') {
        // Get all workers/learners
        const usersResponse = await axiosInstance.get('/admin/users');
        const workers = usersResponse.data.filter(u => u.role === 'worker');
        targetUserIds = workers.map(w => w.id);
      } else if (messageData.recipients === 'workers') {
        // Get workers only
        const usersResponse = await axiosInstance.get('/admin/users');
        const workers = usersResponse.data.filter(u => u.role === 'worker');
        targetUserIds = workers.map(w => w.id);
      } else if (messageData.recipients === 'supervisors') {
        // Get supervisors only
        const usersResponse = await axiosInstance.get('/admin/users');
        const supervisors = usersResponse.data.filter(u => u.role === 'supervisor');
        targetUserIds = supervisors.map(s => s.id);
      } else if (messageData.recipients === 'incomplete') {
        // Get workers with incomplete training (simplified - get all workers for now)
        const usersResponse = await axiosInstance.get('/admin/users');
        const workers = usersResponse.data.filter(u => u.role === 'worker');
        targetUserIds = workers.map(w => w.id);
      } else if (messageData.recipients === 'expiring') {
        // Get workers with expiring certifications (simplified - get all workers for now)
        const usersResponse = await axiosInstance.get('/admin/users');
        const workers = usersResponse.data.filter(u => u.role === 'worker');
        targetUserIds = workers.map(w => w.id);
      }
      
      if (targetUserIds.length === 0) {
        console.error('No recipients found');
        toast.error(t('adminToasts.NoRecipientsFound'));
        setSending(false);
        return;
      }
      
      console.log('Sending message to', targetUserIds.length, 'users');
      
      // Send message with correct field name
      const payload = {
        toUserIds: targetUserIds,
        subject: messageData.subject,
        message: messageData.message
      };
      console.log('Payload:', JSON.stringify(payload, null, 2));
      
      await axiosInstance.post('/admin/send-message', payload);
      
      toast.success(`Messages sent successfully to ${targetUserIds.length} recipient(s)!`);
      setShowMessageDialog(false);
      setMessageData({ recipients: 'all', subject: '', message: '' });
    } catch (error) {
      console.error('Failed to send messages:', error);
      toast.error(error.response?.data?.detail || 'Failed to send messages');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* SafetyMind AI Branding */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 rounded-lg flex items-center justify-center shadow-md">
              <span className="text-white font-bold text-lg">SM</span>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{t('adminUI.SafetymindAi')}</h2>
              <p className="text-xs text-gray-500 italic">{t('adminUI.SafetyThatThinks')}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/admin')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        </div>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{t('adminUI.MessagesNotifications')}</h1>
          <p className="text-gray-600 mt-1">{t('adminUI.SendMessagesTrainingRemindersAndNotificationsToUsers')}</p>
        </div>

        {/* Message Type Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Training Reminders */}
          <Card className="border-2 hover:border-orange-300 transition-colors cursor-pointer" onClick={() => setShowTrainingReminderDialog(true)}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-orange-600" />
                Training Reminders
              </CardTitle>
              <CardDescription>{t('adminUI.SendAutomatedTrainingRemindersToEmployees')}</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• General training reminders</li>
                <li>• Course-specific notifications</li>
                <li>• Certification expiry alerts</li>
                <li>• Bulk messaging to all workers</li>
              </ul>
              <Button className="w-full mt-4 bg-orange-600 hover:bg-orange-700">
                <Bell className="w-4 h-4 mr-2" />
                Send Training Reminder
              </Button>
            </CardContent>
          </Card>

          {/* General Messages */}
          <Card className="border-2 hover:border-blue-300 transition-colors cursor-pointer" onClick={() => setShowMessageDialog(true)}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-blue-600" />
                General Messages
              </CardTitle>
              <CardDescription>{t('adminUI.SendCustomMessagesToWorkersAndSupervisors')}</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Company announcements</li>
                <li>• Policy updates</li>
                <li>• Safety alerts</li>
                <li>• Custom notifications</li>
              </ul>
              <Button className="w-full mt-4 bg-blue-600 hover:bg-blue-700">
                <Send className="w-4 h-4 mr-2" />
                Send Message
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Message Statistics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-gray-600" />
              Message Statistics
            </CardTitle>
            <CardDescription>{t('adminUI.OverviewOfSentMessagesAndNotifications')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Total Messages Sent</div>
                <div className="text-3xl font-bold text-blue-600">0</div>
                <div className="text-xs text-gray-500 mt-1">This month</div>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Training Reminders</div>
                <div className="text-3xl font-bold text-orange-600">0</div>
                <div className="text-xs text-gray-500 mt-1">This month</div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Delivery Rate</div>
                <div className="text-3xl font-bold text-green-600">100%</div>
                <div className="text-xs text-gray-500 mt-1">Last 30 days</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Training Reminder Dialog */}
        <Dialog open={showTrainingReminderDialog} onOpenChange={setShowTrainingReminderDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('adminUI.SendTrainingReminder')}</DialogTitle>
              <DialogDescription>{t('adminUI.SendAutomatedTrainingRemindersToEmployees')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>{t('adminUI.RecipientType')}</Label>
                <Select
                  value={trainingReminderData.recipientType}
                  onValueChange={(value) => setTrainingReminderData({ ...trainingReminderData, recipientType: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select recipients" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Workers</SelectItem>
                    <SelectItem value="incomplete">Workers with Incomplete Training</SelectItem>
                    <SelectItem value="expiring">Workers with Expiring Certifications</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('adminUI.MessageType')}</Label>
                <Select
                  value={trainingReminderData.messageType}
                  onValueChange={(value) => setTrainingReminderData({ ...trainingReminderData, messageType: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select message type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General Training Reminder</SelectItem>
                    <SelectItem value="urgent">Urgent - Complete Training Today</SelectItem>
                    <SelectItem value="certification">Certification Expiring Soon</SelectItem>
                    <SelectItem value="new_course">New Course Available</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <p className="text-sm text-orange-800">
                  <strong>Note:</strong> This will send automated reminders to selected employees via in-app notifications and email.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTrainingReminderDialog(false)} disabled={sending}>
                Cancel
              </Button>
              <Button onClick={handleSendTrainingReminder} disabled={sending} className="bg-orange-600 hover:bg-orange-700">
                {sending ? 'Sending...' : 'Send Reminders'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Send Message Dialog */}
        <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('adminUI.SendMessageToWorkers')}</DialogTitle>
              <DialogDescription>{t('adminUI.SendTrainingRemindersOrImportantNotifications')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>{t('adminUI.Recipients')}</Label>
                <Select
                  value={messageData.recipients}
                  onValueChange={(value) => setMessageData({ ...messageData, recipients: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select recipients" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Workers/Learners</SelectItem>
                    <SelectItem value="workers">Workers Only</SelectItem>
                    <SelectItem value="supervisors">Supervisors Only</SelectItem>
                    <SelectItem value="incomplete">Workers with Incomplete Training</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('adminUI.Subject')}</Label>
                <Input
                  placeholder="Message subject"
                  value={messageData.subject}
                  onChange={(e) => setMessageData({ ...messageData, subject: e.target.value })}
                />
              </div>
              <div>
                <Label>{t('adminUI.Message')}</Label>
                <Textarea
                  placeholder="Your message..."
                  rows={6}
                  value={messageData.message}
                  onChange={(e) => setMessageData({ ...messageData, message: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMessageDialog(false)} disabled={sending}>
                Cancel
              </Button>
              <Button onClick={handleSendMessage} disabled={sending}>
                {sending ? 'Sending...' : 'Send Message'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminMessages;
